import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';

import { atualizarConfiguracao, type Configuracao, type EnderecoAdvogado } from '@/api/secretario';
import { Button } from '@/components/ui/button';
import { CollapsibleCard } from '@/components/ui/CollapsibleCard';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';

import { useWizardSave } from './WizardSaveContext';
import { gravarConfigNoCache } from './cacheDaConfig';

const CAMPOS: { campo: keyof EnderecoAdvogado; rotulo: string; classe?: string; max: number }[] = [
  { campo: 'logradouro', rotulo: 'Logradouro', classe: 'sm:col-span-2', max: 255 },
  { campo: 'numero', rotulo: 'Número', max: 20 },
  { campo: 'complemento', rotulo: 'Complemento', max: 100 },
  { campo: 'bairro', rotulo: 'Bairro', max: 100 },
  { campo: 'cidade', rotulo: 'Cidade', max: 100 },
  { campo: 'uf', rotulo: 'UF', max: 2 },
  { campo: 'cep', rotulo: 'CEP', max: 10 },
];

/**
 * O endereço profissional do escritório: é o que qualifica o advogado no contrato e
 * na procuração quando ele não tem o próprio (``endereco_efetivo``). Sem ele, a área
 * não fecha contrato (a prontidão diz).
 */
export function EnderecoDoEscritorio({
  config,
  open,
  onOpenChange,
}: {
  config: Configuracao;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [endereco, setEndereco] = useState<EnderecoAdvogado>(config.advogado_endereco ?? {});
  const salvar = useMutation({
    mutationFn: () => atualizarConfiguracao({ advogado_endereco: endereco }),
    onSuccess: (atualizada) => {
      gravarConfigNoCache(queryClient, atualizada);
      showSuccess('Endereço salvo.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });
  const salvo = config.advogado_endereco;
  const completo = !!(salvo?.logradouro && salvo.cidade && salvo.uf);
  const alterado = CAMPOS.some(({ campo }) => (endereco[campo] ?? '') !== (salvo?.[campo] ?? ''));

  // Auto-save ao navegar e ao sair do padrão do escritório (nunca perder o digitado).
  const registerFlush = useWizardSave();
  const { mutate, isPending } = salvar;
  useEffect(() => {
    registerFlush('endereco-escritorio', alterado && !isPending ? () => mutate() : null);
    return () => registerFlush('endereco-escritorio', null);
  }, [alterado, isPending, mutate, registerFlush]);

  return (
    <CollapsibleCard
      title="Endereço profissional"
      summary={
        completo ? (
          <span className="flex items-center gap-1 text-emerald-600">
            <Check size={14} /> {salvo?.cidade}/{salvo?.uf}
          </span>
        ) : (
          <span className="text-amber-600">Informe o endereço</span>
        )
      }
      open={open}
      onOpenChange={onOpenChange}
    >
      <p className="text-sm text-muted-foreground">Qualifica os advogados no contrato e na procuração.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {CAMPOS.map(({ campo, rotulo, classe, max }) => (
          <div key={campo} className={`space-y-1.5 ${classe ?? ''}`}>
            <Label htmlFor={`endereco-escritorio-${campo}`}>{rotulo}</Label>
            <Input
              id={`endereco-escritorio-${campo}`}
              value={endereco[campo] ?? ''}
              maxLength={max}
              className="h-11"
              onChange={(e) => setEndereco((atual) => ({ ...atual, [campo]: e.target.value }))}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <Button type="button" onClick={() => salvar.mutate()} disabled={!alterado || salvar.isPending}>
          {salvar.isPending && <Spinner className="mr-2 text-inherit" />}
          Salvar endereço
        </Button>
      </div>
    </CollapsibleCard>
  );
}
